import SearchIcon from '@mui/icons-material/Search'
import Box from '@mui/material/Box'
import InputAdornment from '@mui/material/InputAdornment'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import { useGetAllApplications } from 'api/application/hooks'
import { Application } from 'api/application/types'
import { mapAppStatus } from 'api/application/utils'
import { useGetRole } from 'api/auth/hooks'
import { useGetPublisher } from 'api/publisher/hooks'
import Button from 'components/shared/Button'
import TextField from 'components/shared/inputs/TextField'
import Paper from 'components/shared/Paper'
import { Column } from 'components/shared/Table/types'
import Tag from 'components/shared/Tag'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { colors } from 'styles/theme'
import { storage } from 'utils/localStorage'
import Table from '../../shared/Table'
import AddModal from '../AddModal'
import AppInfo from './AppInfo'

export default function ApplicationTable() {
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [filteredApps, setFilteredApps] = useState<Application[]>([])
  const [searchText, setSearchText] = useState<string>('')
  const { data, isLoading } = useGetAllApplications()

  const { userRole } = useGetRole()

  const publisherId = storage.getValue('publisherId')

  const { data: PublisherData, isLoading: isPublisherLoading } =
    useGetPublisher({ id: publisherId })
  const isPublisherEnabled = PublisherData?.enabled

  const columns = useMemo(getColumns, [])

  const router = useRouter()

  useEffect(() => {
    if (searchText === '') {
      setFilteredApps(data)
      return
    }

    setFilteredApps(
      data?.filter(
        (app) => app.name.toUpperCase().indexOf(searchText.toUpperCase()) > -1
      )
    )
  }, [data, searchText])

  if (userRole === 'publisher' && !isPublisherLoading && !isPublisherEnabled) {
    return (
      <Box padding={2}>
        <Typography fontSize={14} color="red" textAlign="center">
          Your account is currently blocked. Please reach out to your AM for
          more details.
        </Typography>
      </Box>
    )
  }

  return (
    <Paper>
      <Table
        id="application-table"
        loading={isLoading}
        data={filteredApps}
        columns={columns}
        getRowProps={(row) => {
          return {
            style: { cursor: 'pointer' },
            onClick: () => router.push(`/applications/${row.original.id}`),
          }
        }}
        renderToolbar={() => (
          <Toolbar
            sx={{
              borderBottom: `1px solid ${colors.gray[300]}`,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography sx={{ color: `${colors.gray[900]}`, fontWeight: 500 }}>
              Applications
            </Typography>
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <TextField
                sx={{ height: '36px' }}
                placeholder="Search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                }
              />
              <Button
                sx={{ height: '36px' }}
                onClick={() => setModalOpen(true)}
              >
                Add App
              </Button>
            </Box>
          </Toolbar>
        )}
      />
      <AddModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </Paper>
  )
}

function getColumns(): Column[] {
  return [
    {
      Header: 'Name',
      accessor: 'name',
      style: {
        left: 0,
        width: '50%',
      },
      Cell: (props) => <CellName value={props.row.original} />,
    },
    {
      Header: 'Status',
      accessor: 'status',
      style: {
        width: '50%',
      },
      Cell: (props) => <CellStatus value={props.row.original.status} />,
    },
  ]
}

function CellName({ value }) {
  return <AppInfo data={value} />
}

function CellStatus({ value }) {
  const { status: tagColor, text: tagText } = mapAppStatus(value ?? '')
  return <Tag label={tagText} color={tagColor} />
}
